import { FC, useMemo } from "react";
import { ExternalLink, Globe } from "lucide-react";
import LibraryItemRow from "@src/features/libraryItem/module/createModuleContentMembershipMenu/LibraryItemRow";
import { useOpenGraphDataQuery } from "@src/features/contentDetail/ui/MediaPreview.generated";
import { UrlCitationAnnotation, OpenGraphData } from "./types";

// URL validation and sanitization utilities
const isValidUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === "http:" || urlObj.protocol === "https:";
  } catch {
    return false;
  }
};

const sanitizeUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    // Only allow http and https protocols
    if (urlObj.protocol !== "http:" && urlObj.protocol !== "https:") {
      return "#";
    }
    return urlObj.href;
  } catch {
    return "#";
  }
};

interface ChatUrlReferenceProps {
  annotation: UrlCitationAnnotation;
  referenceCount: number;
  citationNumber?: number;
  onClose?: () => void;
}

const getReferenceCountText = (count: number): string => {
  return `${count} reference${count > 1 ? "s" : ""}`;
};

export const ChatUrlReference: FC<ChatUrlReferenceProps> = ({
  annotation,
  referenceCount,
  citationNumber,
  onClose,
}) => {
  const { url, title: titleFromMetadata } = annotation.metadata;
  // Validate and sanitize the URL
  const isValid = useMemo(() => isValidUrl(url), [url]);
  const sanitizedUrl = useMemo(() => sanitizeUrl(url), [url]);

  const { data: ogData, loading } = useOpenGraphDataQuery({
    variables: { url },
    errorPolicy: "ignore", // Don't show errors for failed OG fetches
    skip: !isValid, // Skip query if URL is invalid
  });

  const parsedOgData = useMemo(() => {
    if (!ogData?.OpenGraphData) return null;
    return ogData.OpenGraphData as OpenGraphData;
  }, [ogData]);

  const handlePress = () => {
    if (!isValid) {
      console.warn("Invalid URL:", url);
      return;
    }

    // Close any modals before navigating
    onClose?.();

    // Open sanitized URL in new tab
    window.open(sanitizedUrl, "_blank", "noopener,noreferrer");
  };

  // Create avatar - either OG image or fallback globe icon
  const avatar = useMemo(() => {
    if (!isValid) {
      return (
        <div className="flex size-8 items-center justify-center rounded-lg bg-red-2">
          <Globe className="size-4 text-red-8" />
        </div>
      );
    }

    if (loading) {
      return <div className="size-8 animate-pulse rounded-lg bg-gray-3" />;
    }

    const ogImage = parsedOgData?.["og:image"];
    if (ogImage) {
      return (
        <img
          src={ogImage}
          alt=""
          className="size-8 rounded-lg object-cover"
          onError={(e) => {
            // Fallback to globe icon if image fails to load
            const target = e.target as HTMLImageElement;
            target.style.display = "none";
            const fallback = target.nextElementSibling as HTMLElement;
            if (fallback) {
              fallback.style.display = "flex";
            }
          }}
        />
      );
    }

    return (
      <div className="flex size-8 items-center justify-center rounded-lg bg-blue-2">
        <Globe className="size-4 text-blue-8" />
      </div>
    );
  }, [isValid, loading, parsedOgData]);

  // Create title from OG data, annotation data, or URL fallback
  const title = useMemo(() => {
    if (!isValid) {
      return titleFromMetadata || "Invalid URL";
    }

    if (parsedOgData?.["og:title"]) {
      return parsedOgData["og:title"];
    }
    if (titleFromMetadata) {
      return titleFromMetadata;
    }

    // Extract domain from URL as fallback
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.replace(/^www\./, "");
    } catch {
      return url;
    }
  }, [isValid, parsedOgData, titleFromMetadata, url]);

  // Create subtitle with site name, citation number, and reference count
  const subtitle = useMemo(() => {
    const subtitleParts: string[] = [];

    if (!isValid) {
      subtitleParts.push("Invalid URL");
    } else {
      // Add site name or domain
      const siteName = parsedOgData?.["og:site_name"];
      if (siteName) {
        subtitleParts.push(siteName);
      } else {
        try {
          const urlObj = new URL(url);
          subtitleParts.push(urlObj.hostname.replace(/^www\./, ""));
        } catch {
          subtitleParts.push("External Link");
        }
      }
    }

    // Add citation number
    if (citationNumber) {
      subtitleParts.push(`[${citationNumber}]`);
    }

    // Add reference count
    subtitleParts.push(getReferenceCountText(referenceCount));

    return subtitleParts.join(" • ");
  }, [isValid, citationNumber, referenceCount, parsedOgData, url]);

  return (
    <LibraryItemRow
      avatar={avatar}
      title={title || ""}
      subtitle={subtitle}
      onClick={handlePress}
      rowType="Card"
      showRemoveIcon={false}
      isDisabled={!isValid}
      rightAdornment={
        isValid ? (
          <ExternalLink className="size-4 text-gray-6" />
        ) : (
          <ExternalLink className="size-4 text-red-6" />
        )
      }
    />
  );
};
